import IntlService from 'ember-intl/services/intl';
import moment from 'moment-timezone';
import { IBvTableSort } from 'volta/components/bv-table';
// @ts-ignore
import LabelCell from 'volta/components/cell/label-cell';
import Transaction, { TransactionResourceName } from 'volta/models/transaction';
import LabelService from 'volta/services/label-service';
import { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import { eqFilter } from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs {
  skuId: string;
  transactionDate: Date;
  inModal?: boolean;
  embedded?: boolean;
}

export default class SkuModalTransactionsDetail extends Component<IArgs> {
  // Services
  // ~~~~

  @service intl!: IntlService;
  @service labelService!: LabelService;

  // Properties
  // ~~~~

  resourceName = TransactionResourceName;
  @tracked sorts: IBvTableSort[] = [{ valuePath: 'transactionTs', isAscending: false }];
  @tracked editModalIsVisible = false;
  @tracked selectedTransactions = [];

  // Computed Properties
  // ~~~~~

  @sort('transactions', 'sortProperties')
  sortedTransactions!: Transaction[];

  get sortProperties() {
    return this.sorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  get columns() {
    const intl = this.intl;

    return [
      {
        columnName: intl.t('transactionTs'),
        valuePath: 'transactionTs',
        width: 150,
        isReorderable: true,
        isResizable: true,
        format: ({ transactionTs }: Transaction) => {
          return moment(transactionTs).format('L LTS');
        }
      },
      {
        columnName: intl.t('orderType'),
        valuePath: 'orderType',
        width: 75,
        isReorderable: true,
        isResizable: true,
        cellComponent: LabelCell,
        format: ({ orderType }: Transaction) => {
          return {
            label: orderType,
            color: 'blue'
          };
        }
      },
      {
        columnName: intl.t('qty'),
        valuePath: 'qty',
        width: 75,
        textAlign: 'right',
        classNames: 'u-text-mono',
        isReorderable: true,
        isResizable: true,
        format: (tx: Transaction) => {
          const unit = this.labelService.unitMeasure(tx.sku.part.unitMeasure);
          const qty = tx.qty ? intl.formatNumber(tx.qty) : 0;
          return `${qty} ${unit}`;
        }
      },
      {
        columnName: intl.t('onHandAfter'),
        valuePath: 'onHandAfter',
        textAlign: 'right',
        classNames: 'u-text-mono',
        width: 75,
        isReorderable: true,
        isResizable: true,
        format: (tx: Transaction) => {
          const oh = tx.onHandAfter ? intl.formatNumber(tx.onHandAfter) : 0;
          const unit = this.labelService.unitMeasure(tx.sku.part.unitMeasure);
          return `${oh} ${unit}`;
        }
      },
      {
        columnName: intl.t('orderNum'),
        valuePath: 'orderNum',
        width: 75,
        isReorderable: true,
        isResizable: true,
        cellComponent: LabelCell,
        format: ({ orderNum, orderLine }: Transaction) => {
          return {
            label: orderNum,
            color: 'orange',
            tooltipText: orderLine
          };
        }
      },
      {
        columnName: intl.t('transactionType'),
        valuePath: 'transactionType',
        width: 80,
        isReorderable: true,
        isResizable: true,
        cellComponent: LabelCell,
        format: ({ transactionType }: Transaction) => {
          return {
            label: transactionType
          };
        }
      },
      {
        columnName: intl.t('transactionRef'),
        valuePath: 'transactionRef',
        width: 75,
        isReorderable: true,
        isResizable: true
      }
    ];
  }

  // Actions
  // ~~~~~

  /**
   * Before querying the API for transactions, the <JsonApiCollection/> component will call this function
   * It is used to decorate the query before sending.
   *
   * @param {JsonApiQuery} query JSONAPI formatted query that comes from the JsonApiCollection component
   */
  @action
  serializeQueryFn(query: IJsonApiQuery) {
    query.filter = {
      sku: eqFilter(this.args.skuId)
    };

    if (this.args.transactionDate) {
      query.filter.transactionDate = eqFilter(
        moment.utc(this.args.transactionDate).format('YYYY-MM-DD')
      );
    }

    return query;
  }
}
